import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Campaign,
  Help,
  HelpTwoTone,
  Home,
  ManageAccounts,
  QuestionMarkOutlined,
  Search,
  Timelapse,
  TrendingUp,
} from '@mui/icons-material';
import PrimaryNavButton from './primary-nav-button';
import { memo, useMemo } from 'react';
import { Box, Button, Grid, Modal, Typography } from '@mui/material';
import useUIStore from '../../../stores/ui';
import { useUserStore } from '@oai-frontend-monorepo/state-management';
import RegistrationLogo from '../../icons/logo/full-text-logo';
import SearchComponent from '../../../features/underlying-search/components/search.component';
import NavUnreadCountComponent from '../../../features/notifications/nav-unread-count.component';
import { useLocation } from 'react-router-dom';
import { queryClient } from '@oai-frontend-monorepo/api';
import PaperAccountHeaderComponent from '../../../features/subscribers/components/paper-account-header.component';
import { useOktaAuth } from '@okta/okta-react';
import QuestionModalContent from '../../../features/user-question/question-modal-content';
import { useIsSubscriber } from '../../../features/subscribers/useIsSubscriber';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface Props {
  children: React.ReactNode;
}

const SidePanel = ({ children }: Props) => {
  const theme = useTheme();
  const isSubscriber = useIsSubscriber();

  const [questionModalOpen, setQuestionModalOpen] = React.useState(false);

  const handleQuestion = () => {
    setQuestionModalOpen(true);
  };

  const { sidePanelOpen: open, setSidePanelOpen: setOpen } = useUIStore();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const buttons = useMemo(() => {
    if (isSubscriber) {
      return [
        {
          title: 'Dashboard',
          icon: Home,
          link: '/dashboard',
        },
        {
          title: 'Trade',
          icon: Search,
          link: '/trade',
        },
        {
          title: 'Positions',
          icon: TrendingUp,
          link: '/positions',
        },
        {
          title: 'Price Alerts',
          icon: Campaign,
          link: '/price-alerts',
        },
        {
          title: 'Account',
          icon: ManageAccounts,
          link: '/account',
        },
      ];
    }

    const initialValues = [
      {
        title: 'Dashboard',
        icon: Home,
        link: '/dashboard',
      },
      {
        title: 'Trade',
        icon: Search,
        link: '/trade',
      },
      {
        title: 'Positions',
        icon: TrendingUp,
        link: '/positions',
      },
      {
        title: 'Orders',
        icon: Timelapse,
        link: '/orders',
      },
      {
        title: 'Price Alerts',
        icon: Campaign,
        link: '/price-alerts',
      },
      {
        title: 'Notifications',
        icon: NavUnreadCountComponent,
        link: '/notifications',
      },
      {
        title: 'Account',
        icon: ManageAccounts,
        link: '/account',
      },
    ];

    return initialValues;
  }, [isSubscriber]);

  const location = useLocation();

  const currentPath = location?.pathname?.split('/')?.[1] || '';

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        style={{
          backgroundColor: '#fff',
          boxShadow: 'none',
          borderBottom: '.5px solid rgba(128, 128, 128, 0.35)',
        }}
        position="fixed"
        open={open}
      >
        <Toolbar>
          <Grid
            container
            spacing={0}
            alignItems={'center'}
            justifyContent="space-between"
          >
            <Grid item>
              <Box display="flex" alignItems="center">
                <Box>
                  <IconButton
                    color="secondary"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    edge="start"
                    sx={{
                      marginRight: 5,
                      ...(open && { display: 'none' }),
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>

                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  mt={-0.25}
                >
                  <RegistrationLogo textColor={'#333'} />
                  <PaperAccountHeaderComponent mt={0.5} ml={1.5} />
                </Box>
              </Box>
            </Grid>

            <Grid item>
              <Box display={'flex'} alignItems={'center'}>
                <Box>
                  <Box
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={handleQuestion}
                    border={'1px solid #FFC700'}
                    p={1}
                    borderRadius={1.5}
                    display="flex"
                    alignItems="center"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.9502 0C16.1868 0.000105393 17.9999 1.81321 18 4.0498V13.9502C17.9999 16.1868 16.1868 17.9999 13.9502 18H4.0498C1.81321 17.9999 0.000105393 16.1868 0 13.9502V4.0498C0.00010578 1.81321 1.81321 0.000105587 4.0498 0H13.9502ZM8.90332 12.8984C8.63435 12.8985 8.37578 13.0059 8.18555 13.1963C7.99541 13.3866 7.88867 13.6451 7.88867 13.9141C7.88876 14.1829 7.99563 14.4407 8.18555 14.6309C8.37576 14.8211 8.63435 14.9286 8.90332 14.9287C9.17239 14.9287 9.4308 14.8211 9.62109 14.6309C9.81119 14.4407 9.91788 14.1831 9.91797 13.9141C9.91797 13.645 9.81138 13.3866 9.62109 13.1963C9.43082 13.0059 9.17238 12.8984 8.90332 12.8984ZM8.88281 3.27246C8.23711 3.27609 7.60557 3.46244 7.06055 3.80859C6.51566 4.15512 5.80176 5.23047 5.80176 5.23047C5.80013 5.23687 5.67009 5.75079 5.75781 6.00195C5.84597 6.2546 6.03125 6.4621 6.27246 6.57812H6.25781C6.49879 6.69454 6.77662 6.71032 7.0293 6.62207C7.28195 6.53391 7.4893 6.34863 7.60547 6.10742C7.7209 5.86416 7.90215 5.65796 8.12891 5.5127C8.35602 5.3674 8.6201 5.289 8.88965 5.28613C9.26406 5.29097 9.62284 5.43843 9.89258 5.69824C10.1621 5.95809 10.323 6.3107 10.3418 6.68457C10.3515 6.99265 10.2618 7.29647 10.0859 7.5498C9.91035 7.80291 9.65784 7.99278 9.36621 8.0918C8.94194 8.2305 8.57144 8.4993 8.30762 8.85938C8.04399 9.21921 7.89916 9.65261 7.89453 10.0986V10.9043C7.89459 11.2648 8.08726 11.5979 8.39941 11.7783C8.71177 11.9587 9.09684 11.9587 9.40918 11.7783C9.72151 11.5979 9.91401 11.2649 9.91406 10.9043V10.0986C9.9143 10.0409 10.0091 10.0068 10.0146 10.0049C10.7153 9.76962 11.322 9.31474 11.7441 8.70801C12.1664 8.10138 12.3814 7.37445 12.3584 6.63574C12.3252 5.73845 11.9482 4.88789 11.3047 4.26172C10.6613 3.63564 9.80098 3.28137 8.90332 3.27246H8.88281Z"
                        fill="#FFC700"
                      />
                    </svg>

                    <Typography
                      sx={{
                        ml: 1,
                        color: '#FFC700',
                        fontWeight: 700,
                        fontSize: 15.5,
                      }}
                    >
                      Ask The Team
                    </Typography>
                  </Box>
                </Box>

                <Box
                  position={'relative'}
                  bgcolor={'#fff'}
                  color="#000"
                  minWidth={400}
                  ml={3}
                >
                  <SearchComponent />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <List>
          {buttons.map((button) => (
            <PrimaryNavButton
              key={button.title}
              title={button.title}
              Icon={button.icon}
              isExpanded={open}
              link={button.link}
              active={currentPath === button.link.split('/')[1]}
            />
          ))}
        </List>

        {open && (
          <Box mx={2} mt={4}>
            <Box
              onClick={handleQuestion}
              sx={{
                cursor: 'pointer',
              }}
              display="flex"
              alignItems="center"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.9502 0C16.1868 0.000105393 17.9999 1.81321 18 4.0498V13.9502C17.9999 16.1868 16.1868 17.9999 13.9502 18H4.0498C1.81321 17.9999 0.000105393 16.1868 0 13.9502V4.0498C0.00010578 1.81321 1.81321 0.000105587 4.0498 0H13.9502ZM8.90332 12.8984C8.63435 12.8985 8.37578 13.0059 8.18555 13.1963C7.99541 13.3866 7.88867 13.6451 7.88867 13.9141C7.88876 14.1829 7.99563 14.4407 8.18555 14.6309C8.37576 14.8211 8.63435 14.9286 8.90332 14.9287C9.17239 14.9287 9.4308 14.8211 9.62109 14.6309C9.81119 14.4407 9.91788 14.1831 9.91797 13.9141C9.91797 13.645 9.81138 13.3866 9.62109 13.1963C9.43082 13.0059 9.17238 12.8984 8.90332 12.8984ZM8.88281 3.27246C8.23711 3.27609 7.60557 3.46244 7.06055 3.80859C6.51566 4.15512 5.80176 5.23047 5.80176 5.23047C5.80013 5.23687 5.67009 5.75079 5.75781 6.00195C5.84597 6.2546 6.03125 6.4621 6.27246 6.57812H6.25781C6.49879 6.69454 6.77662 6.71032 7.0293 6.62207C7.28195 6.53391 7.4893 6.34863 7.60547 6.10742C7.7209 5.86416 7.90215 5.65796 8.12891 5.5127C8.35602 5.3674 8.6201 5.289 8.88965 5.28613C9.26406 5.29097 9.62284 5.43843 9.89258 5.69824C10.1621 5.95809 10.323 6.3107 10.3418 6.68457C10.3515 6.99265 10.2618 7.29647 10.0859 7.5498C9.91035 7.80291 9.65784 7.99278 9.36621 8.0918C8.94194 8.2305 8.57144 8.4993 8.30762 8.85938C8.04399 9.21921 7.89916 9.65261 7.89453 10.0986V10.9043C7.89459 11.2648 8.08726 11.5979 8.39941 11.7783C8.71177 11.9587 9.09684 11.9587 9.40918 11.7783C9.72151 11.5979 9.91401 11.2649 9.91406 10.9043V10.0986C9.9143 10.0409 10.0091 10.0068 10.0146 10.0049C10.7153 9.76962 11.322 9.31474 11.7441 8.70801C12.1664 8.10138 12.3814 7.37445 12.3584 6.63574C12.3252 5.73845 11.9482 4.88789 11.3047 4.26172C10.6613 3.63564 9.80098 3.28137 8.90332 3.27246H8.88281Z"
                  fill="#FFC700"
                />
              </svg>

              <Typography
                sx={{
                  ml: 1,
                  color: '#FFC700',
                  fontWeight: 900,
                  fontSize: 15.5,
                }}
              >
                Ask The Team
              </Typography>
            </Box>
            <Typography
              fontSize={12}
              mt={0.5}
              maxWidth={180}
              color={'text.secondary'}
              sx={{
                textOverflow: 'ellipsis',
              }}
            >
              Your trading questions answered
            </Typography>
          </Box>
        )}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }} maxWidth={'100vw'}>
        <DrawerHeader />
        {children}

        <Modal
          open={questionModalOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClose={() => {
            return setQuestionModalOpen(false);
          }}
        >
          <QuestionModalContent onClose={() => setQuestionModalOpen(false)} />
        </Modal>
      </Box>
    </Box>
  );
};

export default memo(SidePanel);
